import React, { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import Modal from "components/shared/Modal";
import { useUpdateProperty, PropertyConfigResponse } from "./hooks/usePropertyData";
import { Button } from "components/ui/button";
import { Checkbox } from "components/ui/checkbox";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Spinner } from "components/ui/spinner";

interface PropertyEditModalProps {
  show: boolean;
  onHide: () => void;
  propertyExternalId: string;
  currentConfig?: PropertyConfigResponse;
}

export default function PropertyEditModal({
  show,
  onHide,
  propertyExternalId,
  currentConfig,
}: PropertyEditModalProps) {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [physicalKeyExtension, setPhysicalKeyExtension] = useState(false);

  const updateMutation = useUpdateProperty(propertyExternalId);

  useEffect(() => {
    if (show) {
      setName(currentConfig?.name ?? "");
      setPhysicalKeyExtension(!!currentConfig?.physicalKeyExtension);
    }
  }, [show, currentConfig]);

  const handleClose = () => {
    onHide();
  };

  const handleSave = () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      enqueueSnackbar("Name is required", { variant: "warning" });
      return;
    }

    updateMutation.mutate(
      { name: trimmedName, physicalKeyExtension },
      {
        onSuccess: () => {
          enqueueSnackbar("Property updated successfully", { variant: "success" });
          queryClient.invalidateQueries({ queryKey: ["property-config", propertyExternalId] });
          queryClient.invalidateQueries({ queryKey: ["properties"] });
          handleClose();
        },
        onError: (error: any) => {
          if (error?.response?.status === 404) {
            enqueueSnackbar("This property no longer exists. It may have been deleted.", { variant: "error" });
            queryClient.invalidateQueries({ queryKey: ["properties"] });
            handleClose();
            return;
          }
          const message = error?.response?.data?.message || error?.message || "Failed to update property";
          enqueueSnackbar(message, { variant: "error" });
        },
      }
    );
  };

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Edit Property</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ paddingTop: 20 }}>
        <div className="flex flex-col gap-4">
          <div className="grid gap-2">
            <Label htmlFor="property-edit-external-id">Property External ID</Label>
            <Input id="property-edit-external-id" value={propertyExternalId} disabled />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="property-edit-name">Name</Label>
            <Input
              id="property-edit-name"
              required
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              checked={physicalKeyExtension}
              id="property-edit-physical-key-extension"
              onCheckedChange={(checked) =>
                setPhysicalKeyExtension(checked === true)
              }
            />
            <Label htmlFor="property-edit-physical-key-extension">
              Physical Key Extension
            </Label>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" variant="outline" onClick={handleClose} disabled={updateMutation.isPending}>
          Cancel
        </Button>
        <Button
          type="button"
          onClick={handleSave}
          disabled={updateMutation.isPending || !name.trim()}
        >
          {updateMutation.isPending ? <Spinner className="size-5" /> : "Save"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
